import { Link } from 'react-router-dom';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-2 mb-6"
          >
            ← Back to Superwhere
          </Link>
          <h1 className="text-4xl font-bold mb-2">Terms and Conditions</h1>
          <p className="text-slate-400">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">1. Agreement to Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              By accessing or using Superwhere ("the Platform," "we," "us," or "our"), you agree to be bound by these Terms and Conditions 
              ("Terms"). If you do not agree to these Terms, please do not use our Platform.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Superwhere is the Global Builder & Superteam Directory for the Solana ecosystem, operated by Belacosaursol. 
              These Terms apply to all users of the Platform, including visitors, registered users, and contributors.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">2. Description of Service</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Superwhere provides a platform for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Discovering and connecting with Solana builders worldwide</li>
              <li>Browsing and joining Superteam communities</li>
              <li>Creating and managing builder profiles</li>
              <li>Representing countries on an interactive global map</li>
              <li>Viewing builder profiles, skills, and links</li>
              <li>Exploring the Solana ecosystem through an interactive 3D globe</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">3. User Accounts and Registration</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">3.1 Account Creation</h3>
            <p className="text-slate-300 leading-relaxed">
              To use certain features of the Platform, you must create an account by authenticating through X/Twitter OAuth. 
              You are responsible for maintaining the confidentiality of your account credentials.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">3.2 Account Requirements</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>You must be at least 13 years old to use the Platform</li>
              <li>You must provide accurate, current, and complete information</li>
              <li>You must not create multiple accounts to circumvent restrictions</li>
              <li>You are responsible for all activities that occur under your account</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">3.3 Account Termination</h3>
            <p className="text-slate-300 leading-relaxed">
              We reserve the right to suspend or terminate your account at any time for violation of these Terms, fraudulent activity, 
              or any other reason we deem necessary to protect the Platform and its users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">4. Acceptable Use</h2>
            <p className="text-slate-300 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Use the Platform for any illegal purpose or in violation of any laws</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Harass, abuse, threaten, or harm other users</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Upload malicious code, viruses, or harmful software</li>
              <li>Attempt to gain unauthorized access to the Platform or other users' accounts</li>
              <li>Interfere with or disrupt the Platform's operation or servers</li>
              <li>Use automated systems (bots, scrapers) to access the Platform without permission</li>
              <li>Violate any intellectual property rights of others</li>
              <li>Collect or harvest information about other users without consent</li>
              <li>Use the Platform to spam, phish, or engage in fraudulent activities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">5. User Content</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">5.1 Your Content</h3>
            <p className="text-slate-300 leading-relaxed">
              You retain ownership of any content you post, upload, or share on the Platform ("User Content"). However, by posting User Content, 
              you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and display your User 
              Content in connection with operating and promoting the Platform.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">5.2 Content Standards</h3>
            <p className="text-slate-300 leading-relaxed mb-4">Your User Content must:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Be accurate and truthful</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not infringe on any third-party rights</li>
              <li>Not contain offensive, discriminatory, or harmful material</li>
              <li>Respect the privacy and dignity of others</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">5.3 Content Moderation</h3>
            <p className="text-slate-300 leading-relaxed">
              We reserve the right to review, edit, or remove any User Content that violates these Terms or that we determine is harmful, 
              inappropriate, or otherwise objectionable. We are not obligated to monitor User Content but may do so at our discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">6.1 Platform Content</h3>
            <p className="text-slate-300 leading-relaxed">
              The Platform, including its design, features, functionality, and content (excluding User Content), is owned by Belacosaursol 
              and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create 
              derivative works without our express written permission.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">6.2 Third-Party Content</h3>
            <p className="text-slate-300 leading-relaxed">
              The Platform may include content, features, or services provided by third parties. Such content is subject to the respective 
              third party's terms and conditions. We are not responsible for third-party content or services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">7. Solana Blockchain and Cryptocurrency</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Superwhere is built for the Solana ecosystem. If you interact with blockchain features:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>You are solely responsible for your cryptocurrency transactions and wallet security</li>
              <li>We are not responsible for any losses resulting from blockchain transactions</li>
              <li>Cryptocurrency transactions are irreversible</li>
              <li>You must comply with all applicable laws regarding cryptocurrency in your jurisdiction</li>
              <li>We do not provide financial, investment, or trading advice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">8. Disclaimers and Limitations of Liability</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">8.1 Service Availability</h3>
            <p className="text-slate-300 leading-relaxed">
              The Platform is provided "as is" and "as available." We do not guarantee that the Platform will be uninterrupted, error-free, 
              or free from viruses or other harmful components. We reserve the right to modify, suspend, or discontinue the Platform at any time.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">8.2 No Warranties</h3>
            <p className="text-slate-300 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES 
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">8.3 Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
              DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR 
              OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE PLATFORM.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">9. Indemnification</h2>
            <p className="text-slate-300 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Belacosaursol, its affiliates, officers, directors, employees, and agents 
              from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of 
              or in any way connected with your use of the Platform, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">10. Privacy</h2>
            <p className="text-slate-300 leading-relaxed">
              Your use of the Platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your information. 
              Please review our <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link> to understand our practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">11. Termination</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may terminate or suspend your access to the Platform immediately, without prior notice, for any reason, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Requests by law enforcement or government agencies</li>
              <li>Extended periods of inactivity</li>
              <li>Discontinuation of the Platform</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              Upon termination, your right to use the Platform will cease immediately. We may delete your account and User Content at our discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">12. Governing Law and Dispute Resolution</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable laws. Any disputes arising out of or relating to these 
              Terms or the Platform shall be resolved through good faith negotiation. If negotiation fails, disputes may be resolved through 
              binding arbitration or in a court of competent jurisdiction, as applicable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">13. Changes to Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on 
              this page and updating the "Last updated" date. Your continued use of the Platform after such changes constitutes acceptance of 
              the updated Terms. If you do not agree to the changes, you must stop using the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">14. Severability</h2>
            <p className="text-slate-300 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum 
              extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">15. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/20">
              <p className="text-slate-300">
                <strong className="text-purple-300">Publisher:</strong> Belacosaursol
              </p>
              <p className="text-slate-300 mt-2">
                <strong className="text-purple-300">Email:</strong>{' '}
                <a href="mailto:architecture@obeliskprotocol.io" className="text-purple-400 hover:underline">
                  architecture@obeliskprotocol.io
                </a>
              </p>
              <p className="text-slate-300 mt-2">
                <strong className="text-purple-300">Support Email:</strong>{' '}
                <a href="mailto:architecture@obeliskprotocol.io" className="text-purple-400 hover:underline">
                  architecture@obeliskprotocol.io
                </a>
              </p>
              <p className="text-slate-300 mt-2">
                <strong className="text-purple-300">Website:</strong>{' '}
                <a href="https://superwhere.io" className="text-purple-400 hover:underline">
                  https://superwhere.io
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">16. Entire Agreement</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Belacosaursol regarding your use 
              of the Platform and supersede all prior agreements and understandings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
